<template>

  <div class="viewer-bar">
    <transition-group :name="viewerTransitionName" tag="div" appear class="viewer-list"
                      @after-enter="afterViewerEnter">
      <div v-for="(viewer, index) in otherViewers" :key="`viewer-${viewer.userId}`" class="viewer-item"
           :style="viewerItemStyle(index)">
        <WidgetViewer :viewer="viewer"
                      :show-notif="Boolean(newViewerNotifs[String(viewer.userId)])"
                      @dismiss-notif="dismissNewViewerNotif(viewer.userId)"/>
      </div>
    </transition-group>

    <!-- DEBUG LED (switch v-if to use) -->
    <WidgetWebSocketControlPanel v-if="false" @led-pressed="sendCheckMap"/>

  </div>
</template>

<script>
import WidgetViewer from "@/components/commons/widgets/WidgetViewer.vue"
import WidgetWebSocketControlPanel from "@/components/commons/widgets/WidgetWebSocketControlPanel.vue";


export default {
  name: "WidgetPageViewers",
  components: {
    WidgetWebSocketControlPanel: WidgetWebSocketControlPanel,
    WidgetViewer
  },
  inject: ["webSocketService"],
  data() {
    return {
      initialViewerBatchPending: true,
      initialViewerEnteredCount: 0,
      knownViewerIds: [],
      newViewerNotifs: {}
    }
  },
  computed: {
    status() {
      return this.webSocketService.state.status
    },
    viewers() {
      return this.webSocketService.state.viewers
    },
    resourceKey() {
      return this.webSocketService.state.resourceKey
    },
    joined() {
      return this.webSocketService.state.joined
    },
    viewersUpdateVersion() {
      return this.webSocketService.state.viewersUpdateVersion
    },
    viewerTransitionName() {
      // [N] Nom animation du transition-group : initial / en cours
      //     Le transition-group joue d'abord l'animation 'inito' au 1er affichage grâce à sa prop 'appear'
      return this.initialViewerBatchPending ? "inito" : "livo"
    },
    otherViewers() {
      return this.viewers.filter(v => v.userId !== this.currentUser.id)
    },
  },
  watch: {
    resourceKey: {
      immediate: true,

      handler() {
        this.initialViewerBatchPending = true
        this.initialViewerEnteredCount = 0
        this.knownViewerIds = []
        this.newViewerNotifs = {}
      }
    },
    viewersUpdateVersion() {
      this.syncNewViewers()
    }
  },
  methods: {
    sendCheckMap() {
      if (!this.isAdmin) { return }
      this.webSocketService.sendCheckMap()
    },
    afterViewerEnter() {
      if (!this.initialViewerBatchPending) { return }

      this.initialViewerEnteredCount += 1
      // [N] Les animations INITO des viewers déjà présent sont comptés 1 par 1
      if (this.initialViewerEnteredCount >= this.otherViewers.length) {
        // [N] Un fois toutes ces animations finies, on passe en mode animation LIVO
        this.initialViewerBatchPending = false
        this.knownViewerIds = this.otherViewers.map(v => String(v.userId))
      }
    },
    viewerItemStyle(index) {
      if (!this.initialViewerBatchPending) { return {} }
      return { animationDelay: `${index * 180}ms` }
    },
    syncNewViewers() {
      const currentViewers = this.otherViewers
      const currentViewerIds = currentViewers.map(v => String(v.userId))

      if (this.initialViewerBatchPending) {
        this.knownViewerIds = currentViewerIds

        if (currentViewerIds.length === 0) {
          // [N] Le booléen d'initialisation sera toggle par l'animation sinon (afterViewerEnter)
          this.initialViewerBatchPending = false
        }
        return
      }

      const previousViewerIds = new Set(this.knownViewerIds)
      const currentViewerIdsSet = new Set(currentViewerIds)

      // [N] +1 notif viewer
      currentViewers.forEach(viewer => {
        const viewerId = String(viewer.userId)
        if (!previousViewerIds.has(viewerId)) {
          this.$set(this.newViewerNotifs, viewerId, viewer)
        }
      })

      // [N] -1 notif viewer
      Object.keys(this.newViewerNotifs).forEach(viewerId => {
        if (!currentViewerIdsSet.has(viewerId)) {
          this.$delete(this.newViewerNotifs, viewerId)
        }
      })

      this.knownViewerIds = currentViewerIds
    },

    dismissNewViewerNotif(userId) {
      this.$delete(this.newViewerNotifs, String(userId))
    },

  }
}
</script>

<style scoped>
.websocket-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: pointer;
}

.viewer-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.viewer-list {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.viewer-item {
  position: relative;
  display: flex;
  transform-origin: center;
  will-change: transform, opacity;
}

.viewer-notif-box {
  position: absolute;
  z-index: 10;
  top: calc(100% + 8px);
  left: 50%;

  width: max-content;
  max-width: 240px;
  padding: 8px 12px;

  transform: translateX(-50%);

  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;

  font-size: 12px;
  line-height: 16px;
  text-align: left;

  cursor: pointer;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.14);
}

.viewer-notif-box::before {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;

  transform: translateX(-50%);

  border-right: 6px solid transparent;
  border-bottom: 6px solid white;
  border-left: 6px solid transparent;
}

/* ---------------- A N I M A T I O N S ----------------------- */

/* [INITO]  Animation initiale */
.inito-enter-active,
.inito-appear-active {
  animation-name: pop;
  animation-duration: 360ms;
  animation-timing-function: ease-out;
  animation-fill-mode: both;
}

@keyframes pop {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }

  70% {
    opacity: 1;
    transform: scale(1.15);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}


/* [LIVO]  Animation +1 viewer */
.livo-enter-active {
  animation: plus-one 520ms ease-out both;
}

@keyframes plus-one {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }

  45% {
    opacity: 1;
    transform: scale(1.15);
  }

  60% {
    transform:
        scale(1)
        translateX(-2px)
        rotate(-4deg);
  }

  72% {
    transform:
        scale(1)
        translateX(2px)
        rotate(4deg);
  }

  84% {
    transform:
        scale(1)
        translateX(-1px)
        rotate(-2deg);
  }

  100% {
    opacity: 1;
    transform:
        scale(1)
        translateX(0)
        rotate(0);
  }
}

/* [LIVO] Animation -1 viewer */
.livo-leave-active {
  animation: minus-one 420ms ease-in both;
  pointer-events: none;
}

@keyframes minus-one {
  0% {
    opacity: 1;
    transform:
        scale(1)
        translateX(0)
        rotate(0);
  }

  15% {
    transform:
        scale(1)
        translateX(-2px)
        rotate(-4deg);
  }

  30% {
    transform:
        scale(1)
        translateX(2px)
        rotate(4deg);
  }

  45% {
    transform:
        scale(1)
        translateX(-1px)
        rotate(-2deg);
  }

  100% {
    opacity: 0;
    transform:
        scale(0.3)
        translateX(0)
        rotate(0);
  }
}
</style>